import React, { useEffect, useState } from 'react';
import Sidebar from './sidebar';
import Header from './header';
import { useAuth } from '../../provider/auth.provider';
import api from '../../libs/axios';
import { FiAlertTriangle, FiX } from 'react-icons/fi';

interface MainLayoutProps {
  children: React.ReactNode;
}

const PASSWORD_EXPIRY_WARNING_DAYS = 14;

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [expiryWarning, setExpiryWarning] = useState<{ days: number } | null>(null);
  const [warnDismissed, setWarnDismissed] = useState(false);
  const { logout } = useAuth();

  useEffect(() => {
    api.get('/auth/password-status').then(res => {
      const d = res.data?.data;
      if (!d) return;
      if (d.days_until_expiry !== null && d.days_until_expiry <= PASSWORD_EXPIRY_WARNING_DAYS && d.days_until_expiry > 0) {
        setExpiryWarning({ days: d.days_until_expiry });
      }
    }).catch(() => {});
  }, []);

  return (
    <div className="h-screen bg-gray-50 flex">
      <Sidebar
        collapsed={sidebarCollapsed}
        mobileOpen={mobileSidebarOpen}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onCloseMobile={() => setMobileSidebarOpen(false)}
        onLogout={logout}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onOpenMobileSidebar={() => setMobileSidebarOpen(true)} />

        {/* Password expiry warning banner */}
        {expiryWarning && !warnDismissed && (
          <div className="flex items-center gap-3 bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-800">
            <FiAlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" />
            <span>
              Your password expires in <strong>{expiryWarning.days} day{expiryWarning.days !== 1 ? 's' : ''}</strong>.
              {' '}Change it in your profile before it expires.
            </span>
            <button
              onClick={() => setWarnDismissed(true)}
              className="ml-auto text-amber-600 hover:text-amber-800">
              <FiX className="h-4 w-4" />
            </button>
          </div>
        )}

        <main className="flex-1 bg-gray-50 mx-auto p-1 sm:p-2 md:p-4 lg:p-6 w-full overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;